import { parseGIF, decompressFrames } from 'gifuct-js';
import GIF from 'gif.js';

const readAsArrayBuffer = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });

const cropGif = async (file, cropPixels, outputSize) => {
  const buffer = await readAsArrayBuffer(file);
  const gif = parseGIF(buffer);
  const frames = decompressFrames(gif, true);

  if (!frames.length) throw new Error('No frames in GIF');

  const { width: srcW, height: srcH } = frames[0].dims;
  const cropX = Math.max(0, Math.round(cropPixels.x));
  const cropY = Math.max(0, Math.round(cropPixels.y));
  const cropW = Math.max(1, Math.round(cropPixels.width));
  const cropH = Math.max(1, Math.round(cropPixels.height));

  // outputSize, when provided, is treated as a MINIMUM. If the crop region is
  // larger, keep the natural size; if smaller, upscale to the minimum so the
  // output isn't tiny. Aspect is already enforced by the cropper.
  const minW = Math.round(outputSize?.width || 0);
  const minH = Math.round(outputSize?.height || 0);
  const outW = Math.max(1, cropW < minW ? minW : cropW);
  const outH = Math.max(1, cropH < minH ? minH : cropH);

  // Full-size canvas to composite frames (handles disposal/offsets correctly)
  const fullCanvas = document.createElement('canvas');
  fullCanvas.width = srcW;
  fullCanvas.height = srcH;
  const fullCtx = fullCanvas.getContext('2d');

  // Output canvas — crop region drawn here, scaled to output dimensions
  const cropCanvas = document.createElement('canvas');
  cropCanvas.width = outW;
  cropCanvas.height = outH;
  const cropCtx = cropCanvas.getContext('2d');
  cropCtx.imageSmoothingEnabled = true;
  cropCtx.imageSmoothingQuality = 'high';

  const encoder = new GIF({
    workers: 2,
    quality: 10,
    width: outW,
    height: outH,
    workerScript: '/gif.worker.js',
    transparent: null,
  });

  let prevImageData = null;

  for (const frame of frames) {
    const { dims, patch, delay, disposalType } = frame;

    // Save state for disposal handling
    if (disposalType === 3) {
      prevImageData = fullCtx.getImageData(0, 0, srcW, srcH);
    }

    // Paint the patch onto the full canvas at the frame's offset
    const patchCanvas = document.createElement('canvas');
    patchCanvas.width = dims.width;
    patchCanvas.height = dims.height;
    const patchCtx = patchCanvas.getContext('2d');
    const imageData = patchCtx.createImageData(dims.width, dims.height);
    imageData.data.set(patch);
    patchCtx.putImageData(imageData, 0, 0);
    fullCtx.drawImage(patchCanvas, dims.left, dims.top);

    // Copy the crop region from the composite into the output canvas,
    // scaling from (cropW x cropH) to (outW x outH).
    cropCtx.clearRect(0, 0, outW, outH);
    cropCtx.drawImage(
      fullCanvas,
      cropX, cropY, cropW, cropH,
      0, 0, outW, outH
    );

    encoder.addFrame(cropCtx, { copy: true, delay: delay || 100 });

    // Apply disposal for next frame
    if (disposalType === 2) {
      fullCtx.clearRect(dims.left, dims.top, dims.width, dims.height);
    } else if (disposalType === 3 && prevImageData) {
      fullCtx.putImageData(prevImageData, 0, 0);
    }
  }

  return new Promise((resolve, reject) => {
    encoder.on('finished', (blob) => {
      const cropped = new File([blob], file.name, { type: 'image/gif' });
      resolve(cropped);
    });
    encoder.on('abort', () => reject(new Error('GIF encoding aborted')));
    encoder.render();
  });
};

export default cropGif;
